import * as t from "io-ts"

// ******************************************************************
// Publish
// ******************************************************************/

export const PublishEvacDevicePostBody = t.type({
    device_type: t.literal("EVAC"),
    device_data: t.intersection([
        t.type({
            Make: t.string,
            Model: t.string,
            ModelReference: t.string,
            Phases: t.number,
            Voltage: t.number,
            MaxCurrentAmpPerPhase: t.number,
            ACOnlyOutput: t.union([t.literal("Yes"), t.literal("No")]),
            V2G_Capability: t.union([t.literal("Yes"), t.literal("No")]),
            Attachment_UUID: t.string,
        }),
        t.partial({
            ManufacturerID: t.string,
            SystemReference: t.string,
            BarcodeNumber: t.string,
            V2G_ExportCapacity: t.number,
            V2G_CompletionDate: t.string,
            V2G_G99GB_G99NI_Both: t.string,
            V2G_TTRStatus: t.string,
            Comments: t.string,
        }),
    ]),
})

export type TPublishEvacDevicePostBody = t.TypeOf<
    typeof PublishEvacDevicePostBody
>

// ******************************************************************
// Amend
// ******************************************************************/

// Align with NewEvacDeviceVersionResponse
export const NewEvacDeviceAmendmentResponse = t.type({
    SystemReference: t.string,
    ManufacturerID: t.string,
    Make: t.string,
    Model: t.string,
    ModelReference: t.string,
    BarcodeNumber: t.union([t.string, t.null]),
    Phases: t.number,
    Voltage: t.number,
    MaxCurrentAmpPerPhase: t.number,
    ACOnlyOutput: t.string,
    V2G_Capability: t.string,
    V2G_ExportCapacity: t.union([t.number, t.null]),
    V2G_CompletionDate: t.union([t.string, t.null]),
    V2G_G99GB_G99NI_Both: t.union([t.string, t.null]),
    V2G_TTRStatus: t.union([t.string, t.null]),
    Comments: t.union([t.string, t.null]),
    Attachment_UUID: t.string,
})

export const AmendEvacDevicePostBody = t.type({
    device_type: t.literal("EVAC"),
    device_data: t.intersection([
        t.type({
            Make: t.string,
            Model: t.string,
            ModelReference: t.string,
            Phases: t.number,
            Voltage: t.number,
            MaxCurrentAmpPerPhase: t.number,
            ACOnlyOutput: t.union([t.literal("Yes"), t.literal("No")]),
            V2G_Capability: t.union([t.literal("Yes"), t.literal("No")]),
            Attachment_UUID: t.string,
        }),
        t.partial({
            ManufacturerID: t.string,
            SystemReference: t.string,
            BarcodeNumber: t.string,
            V2G_ExportCapacity: t.number,
            V2G_CompletionDate: t.string,
            V2G_G99GB_G99NI_Both: t.string,
            V2G_TTRStatus: t.string,
            Comments: t.string,
        }),
    ]),
})

export type TAmendEvacDevicePostBody = t.TypeOf<typeof AmendEvacDevicePostBody>
export type TNewEvacDeviceAmendmentResponse = t.TypeOf<
    typeof NewEvacDeviceAmendmentResponse
>

// ******************************************************************
// Version
// ******************************************************************/

// Align with NewEvacDeviceAmendmentResponse
export const NewEvacDeviceVersionResponse = t.type({
    SystemReference: t.string,
    ManufacturerID: t.string,
    Make: t.string,
    Model: t.string,
    ModelReference: t.string,
    BarcodeNumber: t.union([t.string, t.null]),
    Phases: t.number,
    Voltage: t.number,
    MaxCurrentAmpPerPhase: t.number,
    ACOnlyOutput: t.string,
    V2G_Capability: t.string,
    V2G_ExportCapacity: t.union([t.number, t.null]),
    V2G_CompletionDate: t.union([t.string, t.null]),
    V2G_G99GB_G99NI_Both: t.union([t.string, t.null]),
    V2G_TTRStatus: t.union([t.string, t.null]),
    Comments: t.union([t.string, t.null]),
    Attachment_UUID: t.string,
})

export type TNewEvacDeviceVersionResponse = t.TypeOf<
    typeof NewEvacDeviceVersionResponse
>

// ******************************************************************
// Drafts
// ******************************************************************/

export const SaveEVACDeviceDraftPostBody = t.type({
    device_type: t.literal("EVAC"),
    device_data: t.partial({
        SystemReference: t.string,
        ManufacturerID: t.string,
        Make: t.string,
        Model: t.string,
        ModelReference: t.string,
        BarcodeNumber: t.string,
        Phases: t.number,
        Voltage: t.number,
        MaxCurrentAmpPerPhase: t.number,
        ACOnlyOutput: t.union([t.literal("Yes"), t.literal("No")]),
        V2G_Capability: t.union([t.literal("Yes"), t.literal("No")]),
        V2G_ExportCapacity: t.number,
        V2G_CompletionDate: t.string,
        V2G_G99GB_G99NI_Both: t.string,
        V2G_TTRStatus: t.string,
        Comments: t.string,
        Attachment_UUID: t.string,
    }),
    is_amendment: t.boolean,
})

export const ContinueEVACDraftResponse = t.type({
    SystemReference: t.union([t.string, t.null]),
    ManufacturerID: t.union([t.string, t.null]),
    Make: t.union([t.string, t.null]),
    Model: t.union([t.string, t.null]),
    ModelReference: t.union([t.string, t.null]),
    BarcodeNumber: t.union([t.string, t.null]),
    Phases: t.union([t.number, t.null]),
    Voltage: t.union([t.number, t.null]),
    MaxCurrentAmpPerPhase: t.union([t.number, t.null]),
    ACOnlyOutput: t.union([t.literal("Yes"), t.literal("No"), t.null]),
    V2G_Capability: t.union([t.literal("Yes"), t.literal("No"), t.null]),
    V2G_ExportCapacity: t.union([t.number, t.null]),
    V2G_CompletionDate: t.union([t.string, t.null]),
    V2G_G99GB_G99NI_Both: t.union([t.string, t.null]),
    V2G_TTRStatus: t.union([t.string, t.null]),
    Comments: t.union([t.string, t.null]),
    Attachment_UUID: t.union([t.string, t.null]),
})

export type TSaveEVACDeviceDraftPostBody = t.TypeOf<
    typeof SaveEVACDeviceDraftPostBody
>

export type TContinueEVACDraftResponse = t.TypeOf<
    typeof ContinueEVACDraftResponse
>

// ******************************************************************
// Other
// ******************************************************************/

export type TEVACDeviceResponseType =
    | TNewEvacDeviceAmendmentResponse
    | TContinueEVACDraftResponse
